import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button, Form, Header, Icon, Input, Message, Modal, Segment } from 'semantic-ui-react';
import { captureEvent } from '../utils/posthog';

// A locale is used only when it has the full copy, including the message template (see
// getTranslations). Templates use {name} and {url}; the server composes its own mail text, so this
// text is only what gets copied.
const translations = {
  en: {
    title: 'Get free access through your school',
    body: 'DCACLab is free for teachers to try, and schools can get an affordable classroom license that unlocks every feature for every student.',
    teacherEmailLabel: "Your teacher's email",
    teacherEmailPlaceholder: 'teacher@school.edu',
    teacherEmailHint: "Use your teacher's address, not your own.",
    studentEmailWarning: "This looks like a student email. Please use your teacher's address.",
    yourNameLabel: 'Your name (optional)',
    yourNamePlaceholder: 'So your teacher knows who asked',
    schoolLabel: 'Your school or university (optional)',
    schoolPlaceholder: 'e.g. Lincoln High School or State University',
    messagePreviewLabel: 'Message preview',
    messageWithName: "Hi,\n\nI'm {name}, and I'm using DCACLab for our circuits class. It's really helpful, but my free access is limited.\n\nCould you request a free classroom license for our class? It only takes a minute: {url}\n\nThanks!",
    messageWithoutName: "Hi,\n\nI'm using DCACLab for our circuits class. It's really helpful, but my free access is limited.\n\nCould you request a free classroom license for our class? It only takes a minute: {url}\n\nThanks!",
    copyMessage: 'Copy message',
    sendToTeacher: 'Send to teacher',
    messageCopied: 'Message copied. Paste it in an email to your teacher.',
    requestSentTitle: 'Request sent!',
    requestSentBody: "We'll email your teacher with information about DCACLab.",
    copyFailed: 'Could not copy automatically. The message is selected below: press Ctrl+C (Cmd+C on Mac) to copy it.',
    sendFailed: 'Could not send the request. Please try copying the message instead.',
    invalidEmail: "Please enter your teacher's email address.",
    continueWithLimited: 'Continue with limited access'
  },
  ar: {
    title: 'احصل على وصول مجاني من خلال مدرستك',
    body: 'DCACLab مجاني للمعلمين لتجربته، ويمكن للمدارس الحصول على ترخيص فصل دراسي بأسعار معقولة يفتح كل ميزة لكل طالب.',
    teacherEmailLabel: 'البريد الإلكتروني لمعلمك',
    teacherEmailPlaceholder: 'teacher@school.edu',
    teacherEmailHint: 'استخدم عنوان بريد معلمك، وليس عنوانك أنت.',
    studentEmailWarning: 'يبدو أن هذا بريد إلكتروني لطالب. يرجى استخدام عنوان بريد معلمك.',
    yourNameLabel: 'اسمك (اختياري)',
    yourNamePlaceholder: 'ليعرف معلمك من أرسل الطلب',
    schoolLabel: 'مدرستك أو جامعتك (اختياري)',
    schoolPlaceholder: 'مثال: مدرسة النهضة الثانوية أو جامعة القدس',
    messagePreviewLabel: 'معاينة الرسالة',
    messageWithName: 'مرحبًا،\n\nأنا {name}، وأستخدم DCACLab في مادة الدوائر الكهربائية. إنه مفيد جدًا، لكن وصولي المجاني محدود.\n\nهل يمكنك طلب ترخيص فصل دراسي مجاني لفصلنا؟ لا يستغرق الأمر سوى دقيقة: {url}\n\nشكرًا!',
    messageWithoutName: 'مرحبًا،\n\nأستخدم DCACLab في مادة الدوائر الكهربائية. إنه مفيد جدًا، لكن وصولي المجاني محدود.\n\nهل يمكنك طلب ترخيص فصل دراسي مجاني لفصلنا؟ لا يستغرق الأمر سوى دقيقة: {url}\n\nشكرًا!',
    copyMessage: 'نسخ الرسالة',
    sendToTeacher: 'إرسال إلى المعلم',
    messageCopied: 'تم نسخ الرسالة. الصقها في بريد إلكتروني إلى معلمك.',
    requestSentTitle: 'تم إرسال الطلب!',
    requestSentBody: 'سنرسل بريدًا إلكترونيًا إلى معلمك مع معلومات حول DCACLab.',
    copyFailed: 'تعذّر النسخ تلقائيًا. الرسالة محددة أدناه: اضغط Ctrl+C (أو Cmd+C على Mac) لنسخها.',
    sendFailed: 'تعذّر إرسال الطلب. يرجى محاولة نسخ الرسالة بدلًا من ذلك.',
    invalidEmail: 'يرجى إدخال البريد الإلكتروني لمعلمك.',
    continueWithLimited: 'المتابعة بوصول محدود'
  },
  de: {
    title: 'Holen Sie sich kostenlosen Zugang über Ihre Schule',
    body: 'DCACLab ist für Lehrer kostenlos zum Ausprobieren, und Schulen können eine erschwingliche Klassenlizenz erhalten, die alle Funktionen für alle Schüler freischaltet.',
    teacherEmailLabel: 'E-Mail des Lehrers',
    messagePreviewLabel: 'Nachrichtenvorschau',
    copyMessage: 'Nachricht kopieren',
    sendToTeacher: 'An Lehrer senden',
    messageCopied: 'Nachricht kopiert — fügen Sie sie in eine E-Mail an Ihren Lehrer ein.',
    requestSentTitle: 'Anfrage gesendet!',
    requestSentBody: 'Wir senden Ihrem Lehrer Informationen über DCACLab.',
    copyFailed: 'Nachricht konnte nicht kopiert werden. Bitte markieren und manuell kopieren.',
    sendFailed: 'Anfrage konnte nicht gesendet werden. Bitte versuchen Sie stattdessen, die Nachricht zu kopieren.',
    invalidEmail: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
    continueWithLimited: 'Mit eingeschränktem Zugriff fortfahren'
  },
  fr: {
    title: 'Obtenez un accès gratuit via votre école',
    body: "DCACLab est gratuit pour les enseignants à essayer, et les écoles peuvent obtenir une licence de classe abordable qui déverrouille toutes les fonctionnalités pour chaque élève.",
    teacherEmailLabel: 'E-mail du professeur',
    messagePreviewLabel: 'Aperçu du message',
    copyMessage: 'Copier le message',
    sendToTeacher: 'Envoyer au professeur',
    messageCopied: 'Message copié — collez-le dans un e-mail à votre professeur.',
    requestSentTitle: 'Demande envoyée !',
    requestSentBody: 'Nous enverrons un e-mail à votre professeur avec des informations sur DCACLab.',
    copyFailed: 'Impossible de copier le message. Veuillez le sélectionner et le copier manuellement.',
    sendFailed: "Impossible d'envoyer la demande. Veuillez essayer de copier le message à la place.",
    invalidEmail: 'Veuillez saisir une adresse e-mail valide.',
    continueWithLimited: 'Continuer avec un accès limité'
  },
  es: {
    title: 'Obtén acceso gratuito a través de tu escuela',
    body: 'DCACLab es gratuito para que los profesores lo prueben, y las escuelas pueden obtener una licencia de clase asequible que desbloquea todas las funciones para cada estudiante.',
    teacherEmailLabel: 'El correo de tu profesor',
    teacherEmailPlaceholder: 'profesor@escuela.edu',
    teacherEmailHint: 'Usa la dirección de tu profesor, no la tuya.',
    studentEmailWarning: 'Parece un correo de estudiante. Usa la dirección de tu profesor.',
    yourNameLabel: 'Tu nombre (opcional)',
    yourNamePlaceholder: 'Para que tu profesor sepa quién lo pidió',
    schoolLabel: 'Tu escuela o universidad (opcional)',
    schoolPlaceholder: 'p. ej., Colegio San Martín o Universidad Nacional',
    messagePreviewLabel: 'Vista previa del mensaje',
    messageWithName: 'Hola,\n\nSoy {name} y estoy usando DCACLab para nuestra clase de circuitos. Me ayuda mucho, pero mi acceso gratuito es limitado.\n\n¿Podrías solicitar una licencia de aula gratuita para nuestra clase? Solo toma un minuto: {url}\n\n¡Gracias!',
    messageWithoutName: 'Hola,\n\nEstoy usando DCACLab para nuestra clase de circuitos. Me ayuda mucho, pero mi acceso gratuito es limitado.\n\n¿Podrías solicitar una licencia de aula gratuita para nuestra clase? Solo toma un minuto: {url}\n\n¡Gracias!',
    copyMessage: 'Copiar mensaje',
    sendToTeacher: 'Enviar al profesor',
    messageCopied: 'Mensaje copiado. Pégalo en un correo a tu profesor.',
    requestSentTitle: '¡Solicitud enviada!',
    requestSentBody: 'Enviaremos un correo a tu profesor con información sobre DCACLab.',
    copyFailed: 'No se pudo copiar automáticamente. El mensaje está seleccionado abajo: pulsa Ctrl+C (Cmd+C en Mac) para copiarlo.',
    sendFailed: 'No se pudo enviar la solicitud. Intenta copiar el mensaje en su lugar.',
    invalidEmail: 'Escribe el correo electrónico de tu profesor.',
    continueWithLimited: 'Continuar con acceso limitado'
  },
  tr: {
    title: 'Okulunuz üzerinden ücretsiz erişim alın',
    body: "DCACLab öğretmenlerin denemesi için ücretsizdir ve okullar, her öğrenci için tüm özelliklerin kilidini açan uygun fiyatlı bir sınıf lisansı alabilir.",
    teacherEmailLabel: 'Öğretmenin e-postası',
    messagePreviewLabel: 'Mesaj önizlemesi',
    copyMessage: 'Mesajı kopyala',
    sendToTeacher: 'Öğretmene gönder',
    messageCopied: 'Mesaj kopyalandı — öğretmeninize bir e-postada yapıştırın.',
    requestSentTitle: 'İstek gönderildi!',
    requestSentBody: 'Öğretmeninize DCACLab hakkında bilgi içeren bir e-posta göndereceğiz.',
    copyFailed: 'Mesaj kopyalanamadı. Lütfen manuel olarak seçin ve kopyalayın.',
    sendFailed: 'İstek gönderilemedi. Bunun yerine mesajı kopyalamayı deneyin.',
    invalidEmail: 'Lütfen geçerli bir e-posta adresi girin.',
    continueWithLimited: 'Sınırlı erişimle devam et'
  },
  th: {
    title: 'รับการเข้าถึงฟรีผ่านโรงเรียนของคุณ',
    body: 'DCACLab ให้ครูใช้ฟรีเพื่อทดลอง และโรงเรียนสามารถขอรับใบอนุญาตห้องเรียนราคาประหยัดที่ปลดล็อกทุกฟีเจอร์สำหรับนักเรียนทุกคน',
    teacherEmailLabel: 'อีเมลครู',
    messagePreviewLabel: 'ตัวอย่างข้อความ',
    copyMessage: 'คัดลอกข้อความ',
    sendToTeacher: 'ส่งถึงครู',
    messageCopied: 'คัดลอกข้อความแล้ว — วางในอีเมลถึงครูของคุณ',
    requestSentTitle: 'ส่งคำขอแล้ว!',
    requestSentBody: 'เราจะส่งอีเมลถึงครูของคุณพร้อมข้อมูลเกี่ยวกับ DCACLab',
    copyFailed: 'ไม่สามารถคัดลอกข้อความได้ โปรดเลือกและคัดลอกด้วยตนเอง',
    sendFailed: 'ไม่สามารถส่งคำขอได้ โปรดลองคัดลอกข้อความแทน',
    invalidEmail: 'โปรดป้อนที่อยู่อีเมลที่ถูกต้อง',
    continueWithLimited: 'ดำเนินการต่อด้วยการเข้าถึงแบบจำกัด'
  }
};
// The lab serves Mexico as its own locale with the Spanish copy
translations.mx = translations.es;

// A block is used only when it carries the message template; a partial block would mix languages
const getTranslations = (locale) => {
  const block = translations[locale];
  return block && block.messageWithoutName ? block : translations.en;
};

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// Domain labels that mark a student mailbox (x@smail.ucas.edu.ps, a@student.uni.edu). Mirrors
// TeacherInvite::STUDENT_DOMAIN_LABELS and TeacherInvite.student_looking_email? in
// app/models/teacher_invite.rb, which stores the flag server-side; keep the lists and rules identical.
const STUDENT_EMAIL_LABELS = ['smail', 'student', 'students', 'stu', 'stud', 'alumnos', 'alumno', 'estudiantes'];

// True when any domain label except the last (the TLD) is a student label, or the local part is
// 6+ digits (a student ID). Only drives a soft warning; the student can still send.
const looksLikeStudentEmail = (email) => {
  const value = (email || '').trim().toLowerCase();
  const at = value.indexOf('@');
  if (at < 1) return false;
  const local = value.slice(0, at);
  const domain = value.slice(at + 1);
  if (!domain) return false;
  if (/^\d{6,}$/.test(local)) return true;
  // filter(Boolean) matches Ruby's split, which drops trailing empty labels
  const labels = domain.split('.').filter(Boolean);
  return labels.slice(0, -1).some((label) => STUDENT_EMAIL_LABELS.includes(label));
};

/**
 * InviteTeacherModal
 *
 * Opened by the student CTA in ActionMessage. The student copies a ready-made message for their
 * teacher, or gives us the teacher's address and we mail our own template from the server
 * (POST /api/invite_teacher). No free text is relayed.
 */
const InviteTeacherModal = ({ open, onClose, current_locale = 'en', user = null }) => {
  const [teacherEmail, setTeacherEmail] = useState('');
  const [studentName, setStudentName] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  const previewRef = useRef(null);
  const copiedTimer = useRef(null);

  const t = getTranslations(current_locale);
  const isRTL = current_locale === 'ar';
  const showStudentWarning = looksLikeStudentEmail(teacherEmail);

  useEffect(() => {
    if (!open) return;
    setTeacherEmail('');
    setStudentName('');
    setSchoolName('');
    setCopied(false);
    setCopyFailed(false);
    setSending(false);
    setSent(false);
    setError(null);
  }, [open]);

  useEffect(() => () => clearTimeout(copiedTimer.current), []);

  const getMessage = () => {
    const url = `${window.location.origin}/${current_locale || 'en'}/request-access?utm_source=student_invite&utm_medium=copy`;
    const name = studentName.trim();
    const template = name ? t.messageWithName : t.messageWithoutName;
    // Function replacers: a typed name containing `$&` or `$1` must not be expanded
    return template.replace('{name}', () => name).replace('{url}', () => url);
  };

  const track = (method) => {
    captureEvent('student_invited_teacher', {
      method,
      locale: current_locale,
      has_student_name: studentName.trim().length > 0,
      has_school_name: schoolName.trim().length > 0,
      teacher_email_looks_student: looksLikeStudentEmail(teacherEmail),
      user_signed_in: !!user
    });
  };

  // Fallback when the Clipboard API is unavailable or refused: leave the text selected for Ctrl+C
  const selectPreview = () => {
    const node = previewRef.current;
    if (!node || !window.getSelection || !document.createRange) return;
    const range = document.createRange();
    range.selectNodeContents(node);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
  };

  const handleCopy = async () => {
    setError(null);
    setCopyFailed(false);
    try {
      if (!navigator.clipboard || !navigator.clipboard.writeText) throw new Error('Clipboard API unavailable');
      await navigator.clipboard.writeText(getMessage());
      setCopied(true);
      track('copy');
      clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => setCopied(false), 4000);
    } catch (e) {
      setCopyFailed(true);
      selectPreview();
    }
  };

  const handleSend = async () => {
    const email = teacherEmail.trim();
    if (!isValidEmail(email)) {
      setError(t.invalidEmail);
      return;
    }
    setSending(true);
    setError(null);
    try {
      // The lab's global axios already carries the CSRF token (LabLayout.js)
      await axios.post('/api/invite_teacher', {
        teacher_email: email,
        student_name: studentName.trim() || undefined,
        school_name: schoolName.trim() || undefined,
        locale: current_locale
      });
      setSent(true);
      track('email');
    } catch (e) {
      const serverError = e && e.response && e.response.data && e.response.data.error;
      setError(serverError || t.sendFailed);
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = (event) => {
    if (event && event.preventDefault) event.preventDefault();
    if (!sending) handleSend();
  };

  return (
    <Modal
      id="invite-teacher-modal"
      open={open}
      onClose={onClose}
      size="tiny"
      closeOnEscape
      closeOnDimmerClick
      aria-label={t.title}
    >
      <Modal.Header dir={isRTL ? 'rtl' : undefined}>
        <Icon name="graduation cap" />
        {t.title}
      </Modal.Header>

      <Modal.Content dir={isRTL ? 'rtl' : undefined}>
        {sent ? (
          <Message positive icon id="invite-teacher-sent" role="status">
            <Icon name="check circle" />
            <Message.Content>
              <Message.Header>{t.requestSentTitle}</Message.Header>
              <p>{t.requestSentBody}</p>
            </Message.Content>
          </Message>
        ) : (
          <>
            <p>{t.body}</p>

            <Form onSubmit={handleSubmit} id="invite-teacher-form">
              <Form.Field required>
                <label htmlFor="invite-teacher-email">{t.teacherEmailLabel}</label>
                <Input
                  id="invite-teacher-email"
                  dir="ltr"
                  type="email"
                  placeholder={t.teacherEmailPlaceholder}
                  value={teacherEmail}
                  onChange={(e, { value }) => setTeacherEmail(value)}
                  aria-label={t.teacherEmailLabel}
                  aria-describedby={showStudentWarning ? 'invite-teacher-email-hint invite-teacher-student-warning' : 'invite-teacher-email-hint'}
                  autoComplete="off"
                  required
                />
                <div id="invite-teacher-email-hint" style={{ marginTop: '0.4em', fontSize: '0.9em', color: 'rgba(0, 0, 0, 0.6)' }}>
                  {t.teacherEmailHint}
                </div>
                {/* `visible`: Semantic hides warning messages inside a form unless the form is in its warning state */}
                {showStudentWarning && (
                  <Message warning visible size="small" role="alert" id="invite-teacher-student-warning" style={{ marginTop: '0.5em' }}>
                    <Icon name="warning sign" />
                    {t.studentEmailWarning}
                  </Message>
                )}
              </Form.Field>
              <Form.Group widths="equal">
                <Form.Input
                  id="invite-teacher-name"
                  label={t.yourNameLabel}
                  placeholder={t.yourNamePlaceholder}
                  value={studentName}
                  onChange={(e, { value }) => setStudentName(value)}
                  aria-label={t.yourNameLabel}
                  maxLength={100}
                />
                <Form.Input
                  id="invite-teacher-school"
                  label={t.schoolLabel}
                  placeholder={t.schoolPlaceholder}
                  value={schoolName}
                  onChange={(e, { value }) => setSchoolName(value)}
                  aria-label={t.schoolLabel}
                  maxLength={100}
                />
              </Form.Group>
            </Form>

            <Header as="h5" style={{ marginTop: '1em', marginBottom: '0.5em' }}>{t.messagePreviewLabel}</Header>
            <Segment secondary style={{ marginTop: 0 }}>
              <pre
                id="invite-teacher-preview"
                ref={previewRef}
                tabIndex={0}
                aria-label={t.messagePreviewLabel}
                style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '0.95em', margin: 0, background: 'transparent', userSelect: 'text' }}
              >
                {getMessage()}
              </pre>
            </Segment>

            {error && <Message negative role="alert" id="invite-teacher-error">{error}</Message>}
            {copied && <Message positive role="status" id="invite-teacher-copied">{t.messageCopied}</Message>}
            {copyFailed && <Message warning role="alert" id="invite-teacher-copy-failed">{t.copyFailed}</Message>}
          </>
        )}
      </Modal.Content>

      <Modal.Actions dir={isRTL ? 'rtl' : undefined}>
        {sent ? (
          <Button id="invite-teacher-continue" primary onClick={onClose} aria-label={t.continueWithLimited}>
            {t.continueWithLimited}
          </Button>
        ) : (
          <>
            <Button id="invite-teacher-continue" basic onClick={onClose} aria-label={t.continueWithLimited}>
              {t.continueWithLimited}
            </Button>
            <Button id="invite-teacher-copy" onClick={handleCopy} aria-label={t.copyMessage}>
              <Icon name="copy" />
              {t.copyMessage}
            </Button>
            <Button
              id="invite-teacher-send"
              primary
              type="submit"
              form="invite-teacher-form"
              loading={sending}
              disabled={sending}
              aria-label={t.sendToTeacher}
            >
              <Icon name="paper plane" />
              {t.sendToTeacher}
            </Button>
          </>
        )}
      </Modal.Actions>
    </Modal>
  );
};

export default InviteTeacherModal;
